'use client';
import { useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import Button from '../ui/Button';
import Logo from '../ui/Logo';
import HeaderMenu from '../ui/HeaderMenu';
import HeaderMenuItem from '../ui/HeaderMenuItem';
import IconButton from '../ui/IconButton';
import Link from 'next/link';

interface HeaderProps {
  className?: string;
}

const Header = ({ className }: HeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  // Sticky effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [menuOpen]);

  const menuItems = [
    { label: 'Home', href: '/', active: true },
    { label: 'Events', href: '/#events', active: false },
    { label: 'Speakers', href: '/#speakers', active: false },
    { label: 'Event Highlights', href: '/#event-highlights', active: false },
    { label: 'Partners', href: '/#partners', active: false },
    { label: 'About Us', href: '/#about', active: false },
  ];

  return (
    <header data-component-id="src/components/common/Header.tsx:47:4" data-component-path="src/components/common/Header.tsx" data-component-line="47" data-component-end-line="172" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D"
      className={twMerge(
        'w-full z-50 transition-all duration-300',
        isSticky
          ? 'fixed top-0 left-0 bg-white shadow-lg'
          : 'absolute top-0 left-0 bg-white',
        'px-4 sm:px-6 md:px-8 lg:px-[120px]',
        'py-4 sm:py-5 md:py-[22px]',
        className
      )}
    >
      <div data-component-id="src/components/common/Header.tsx:58:6" data-component-path="src/components/common/Header.tsx" data-component-line="58" data-component-end-line="134" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20flex%20items-center%20justify-between%20w-full%22%7D" className="relative flex items-center justify-between w-full">
        {/* Logo */}
        <Link data-component-id="src/components/common/Header.tsx:60:8" data-component-path="src/components/common/Header.tsx" data-component-line="60" data-component-end-line="68" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%7D" href='/'>
        <Logo data-component-id="src/components/common/Header.tsx:61:8" data-component-path="src/components/common/Header.tsx" data-component-line="61" data-component-end-line="67" data-component-file="Header.tsx" data-component-name="Logo" data-component-content="%7B%22elementName%22%3A%22Logo%22%2C%22src%22%3A%22%2Fimages%2Fblockver-series-logo.svg%22%2C%22alt%22%3A%22Blockverse%20Series%20Logo%22%2C%22className%22%3A%22flex-shrink-0%20w-%5B140px%5D%20sm%3Aw-%5B160px%5D%20lg%3Aw-auto%22%7D"
          src="/images/blockver-series-logo.svg"
          alt="Blockverse Series Logo"
          variant="header"
          className="flex-shrink-0 w-[140px] sm:w-[160px] lg:w-auto"
          unoptimized
        />
        </Link>
        {/* Desktop Navigation Centered */}
        <HeaderMenu data-component-id="src/components/common/Header.tsx:70:8" data-component-path="src/components/common/Header.tsx" data-component-line="70" data-component-end-line="86" data-component-file="Header.tsx" data-component-name="HeaderMenu" data-component-content="%7B%22elementName%22%3A%22HeaderMenu%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20absolute%20left-1%2F2%20transform%20-translate-x-1%2F2%22%7D" className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2">
          {menuItems.map((item, index) => (
            <HeaderMenuItem data-component-id="src/components/common/Header.tsx:72:12" data-component-path="src/components/common/Header.tsx" data-component-line="72" data-component-end-line="84" data-component-file="Header.tsx" data-component-name="HeaderMenuItem" data-component-content="%7B%22elementName%22%3A%22HeaderMenuItem%22%7D"
              key={index}
              active={item.active}
              href={item.href}
              fontSize="text-sm"
              fontFamily={item.active ? 'Instrument Sans' : 'Inter'}
              fontWeight={item.active ? 'font-bold' : 'font-medium'}
              textColor={
                item.active ? 'text-primary-background' : 'text-text-secondary'
              }
            >
              {item.label}
            </HeaderMenuItem>
          ))}
        </HeaderMenu>

        {/* Right Side Buttons */}
        <div data-component-id="src/components/common/Header.tsx:89:8" data-component-path="src/components/common/Header.tsx" data-component-line="89" data-component-end-line="133" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-4%22%7D" className="flex items-center gap-4">
          {/* Mobile Hamburger */}
          <div data-component-id="src/components/common/Header.tsx:91:10" data-component-path="src/components/common/Header.tsx" data-component-line="91" data-component-end-line="120" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22block%20lg%3Ahidden%22%7D" className="block lg:hidden">
            <IconButton data-component-id="src/components/common/Header.tsx:92:12" data-component-path="src/components/common/Header.tsx" data-component-line="92" data-component-end-line="119" data-component-file="Header.tsx" data-component-name="IconButton" data-component-content="%7B%22elementName%22%3A%22IconButton%22%7D"
              size="medium"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <svg data-component-id="src/components/common/Header.tsx:97:14" data-component-path="src/components/common/Header.tsx" data-component-line="97" data-component-end-line="118" data-component-file="Header.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-6%20h-6%22%7D"
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {menuOpen ? (
                  <path data-component-id="src/components/common/Header.tsx:104:18" data-component-path="src/components/common/Header.tsx" data-component-line="104" data-component-end-line="109" data-component-file="Header.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path data-component-id="src/components/common/Header.tsx:111:18" data-component-path="src/components/common/Header.tsx" data-component-line="111" data-component-end-line="116" data-component-file="Header.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </IconButton>
          </div>

          {/* Desktop Contact Button */}
          <Link data-component-id="src/components/common/Header.tsx:123:10" data-component-path="src/components/common/Header.tsx" data-component-line="123" data-component-end-line="132" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22https%3A%2F%2Fwww.blockverseseries.com%2Fcontact%2F%22%7D" href="https://www.blockverseseries.com/contact/">
            <Button data-component-id="src/components/common/Header.tsx:124:12" data-component-path="src/components/common/Header.tsx" data-component-line="124" data-component-end-line="131" data-component-file="Header.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22hidden%20lg%3Ainline-flex%20px-%5B22px%5D%20py-%5B12px%5D%22%7D"
              text="Contact Us"
              text_font_size="text-[14px] sm:text-[16px] md:text-[18px]"
              text_font_family="font-instrument"
              text_font_weight="font-semibold"
              className="hidden lg:inline-flex px-[22px] py-[12px]"
              variant="primary"
            />
          </Link>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <nav data-component-id="src/components/common/Header.tsx:137:6" data-component-path="src/components/common/Header.tsx" data-component-line="137" data-component-end-line="171" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%7D"
        className={twMerge(
          'lg:hidden absolute top-full left-0 w-full bg-white z-40 shadow-md transition-all duration-300',
          menuOpen ? 'block' : 'hidden'
        )}
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div data-component-id="src/components/common/Header.tsx:145:8" data-component-path="src/components/common/Header.tsx" data-component-line="145" data-component-end-line="170" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20space-y-4%20py-4%20border-t%20border-secondary-light%22%7D" className="flex flex-col space-y-4 py-4 border-t border-secondary-light">
          {menuItems.map((item, index) => (
            <HeaderMenuItem data-component-id="src/components/common/Header.tsx:147:12" data-component-path="src/components/common/Header.tsx" data-component-line="147" data-component-end-line="155" data-component-file="Header.tsx" data-component-name="HeaderMenuItem" data-component-content="%7B%22elementName%22%3A%22HeaderMenuItem%22%2C%22className%22%3A%22text-left%20px-4%20py-2%20w-full%22%7D"
              key={index}
              active={item.active}
              href={item.href}
              className="text-left px-4 py-2 w-full"
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </HeaderMenuItem>
          ))}
          <div data-component-id="src/components/common/Header.tsx:157:10" data-component-path="src/components/common/Header.tsx" data-component-line="157" data-component-end-line="169" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20pt-4%22%7D" className="px-4 pt-4">
            <Link data-component-id="src/components/common/Header.tsx:158:12" data-component-path="src/components/common/Header.tsx" data-component-line="158" data-component-end-line="168" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22https%3A%2F%2Fwww.blockverseseries.com%2Fcontact%2F%22%7D" href="https://www.blockverseseries.com/contact/">
              <Button data-component-id="src/components/common/Header.tsx:159:14" data-component-path="src/components/common/Header.tsx" data-component-line="159" data-component-end-line="167" data-component-file="Header.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22w-full%22%7D"
                text="Contact Us"
                text_font_size="text-[14px] sm:text-[16px] md:text-[18px]"
                text_font_family="font-instrument"
                text_font_weight="font-semibold"
                className="w-full"
                variant="primary"
                onClick={() => setMenuOpen(false)}
              />
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
